import React, { useState } from 'react'
import { Box, Typography, Stack, IconButton, Chip, Button } from '@mui/material'
import SettingsIcon from '@mui/icons-material/Tune'
import SwitchCameraIcon from '@mui/icons-material/Cameraswitch'
import FlashOnIcon from '@mui/icons-material/FlashOn'
import FlashOffIcon from '@mui/icons-material/FlashOff'
import HistoryIcon from '@mui/icons-material/History'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import { useNavigate } from 'react-router-dom'

import { useScan } from '../context/scanContext'
import { ScanModes } from '../utils/scanModeEnum'
import CameraScanner from '../components/scanner/CameraScanner'
import ScanResponse from './ScanResponseView'
import logoSplash from '../assets/logoSplash.png'
import { BRAND } from '../theme/theme'

const HISTORY_ACCENT = '#4C9AFF'

/** Bouton rond translucide posé sur le flux caméra. */
const GlassButton = ({ label, icon, onClick, activeState = false, accent }) => (
  <Stack alignItems="center" spacing={0.75} sx={{ minWidth: 64 }}>
    <IconButton
      onClick={onClick}
      aria-label={label}
      aria-pressed={activeState}
      sx={{
        width: 60,
        height: 60,
        color: activeState ? '#0E0F11' : '#FFFFFF',
        bgcolor: activeState ? accent || BRAND.orange : 'rgba(255,255,255,0.14)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.18)',
        '&:hover': {
          bgcolor: activeState ? accent || BRAND.orange : 'rgba(255,255,255,0.22)',
        },
      }}
    >
      {icon}
    </IconButton>
    <Typography
      variant="caption"
      sx={{ color: 'rgba(255,255,255,0.82)', fontWeight: 600, textAlign: 'center' }}
    >
      {label}
    </Typography>
  </Stack>
)

const ScanView = () => {
  const { scanMode, setCurrentCode, resetScan, controlPoint } = useScan()
  const [openResponse, setOpenResponse] = useState(false)
  const [historyMode, setHistoryMode] = useState(false)
  const navigate = useNavigate()

  const handleScan = (result) => {
    setCurrentCode(result)
    setOpenResponse(true)
  }

  const handleCancel = () => {
    setOpenResponse(false)
    resetScan()
    setHistoryMode(false)
  }

  const toggleHistoryMode = () => setHistoryMode((value) => !value)

  if (openResponse) {
    return <ScanResponse onCancel={handleCancel} isHistoryValidation={historyMode} />
  }

  const accent = historyMode ? HISTORY_ACCENT : BRAND.orange
  const modeLabel =
    scanMode === ScanModes.ACCESS ? "Contrôle d'accès" : 'Consommation de produits'

  return (
    <Box
      sx={{
        position: 'relative',
        height: 'var(--app-vh)',
        width: '100%',
        overflow: 'hidden',
        bgcolor: '#06070A',
      }}
    >
      <CameraScanner onScan={handleScan} accentColor={accent}>
        {({ torch, camera, error }) => (
          <>
            {/* ---------- Bandeau supérieur ---------- */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                pt: 'calc(var(--safe-top) + 10px)',
                pb: 3,
                pl: 'calc(var(--safe-left) + 16px)',
                pr: 'calc(var(--safe-right) + 8px)',
                background:
                  'linear-gradient(180deg, rgba(6,7,10,0.85) 0%, rgba(6,7,10,0.45) 55%, rgba(6,7,10,0) 100%)',
              }}
            >
              <Box
                component="img"
                src={logoSplash}
                alt="TicketNGo"
                sx={{ height: 24, width: 'auto', flexShrink: 0 }}
              />
              <Box sx={{ flexGrow: 1 }} />
              <IconButton
                onClick={() => navigate('/settings')}
                aria-label="Réglages"
                sx={{ color: '#FFFFFF' }}
              >
                <SettingsIcon fontSize="large" />
              </IconButton>
            </Stack>

            {/* ---------- Étiquette de mode ---------- */}
            <Stack
              alignItems="center"
              spacing={1}
              sx={{
                position: 'absolute',
                top: 'calc(var(--safe-top) + 62px)',
                left: 16,
                right: 16,
              }}
            >
              <Chip
                size="small"
                label={historyMode ? "Consultation d'historique" : modeLabel}
                sx={{
                  bgcolor: historyMode ? HISTORY_ACCENT : 'rgba(255,255,255,0.16)',
                  color: historyMode ? '#0E0F11' : '#FFFFFF',
                  backdropFilter: 'blur(12px)',
                  height: 30,
                  px: 0.5,
                }}
              />
              {!historyMode && controlPoint?.Name ? (
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}
                >
                  {controlPoint.Name}
                </Typography>
              ) : null}
            </Stack>

            {/* ---------- Consigne + contrôles ---------- */}
            <Stack
              spacing={2.5}
              alignItems="center"
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                pt: 6,
                pb: 'calc(var(--safe-bottom) + 16px)',
                pl: 'calc(var(--safe-left) + 16px)',
                pr: 'calc(var(--safe-right) + 16px)',
                background:
                  'linear-gradient(0deg, rgba(6,7,10,0.92) 0%, rgba(6,7,10,0.6) 55%, rgba(6,7,10,0) 100%)',
              }}
            >
              {!error ? (
                <Typography
                  variant="h6"
                  sx={{
                    color: '#FFFFFF',
                    textAlign: 'center',
                    textShadow: '0 2px 12px rgba(0,0,0,0.6)',
                  }}
                >
                  {historyMode
                    ? 'Balayez un billet pour voir son historique'
                    : 'Balayez le code du billet'}
                </Typography>
              ) : null}

              <Stack direction="row" spacing={3} justifyContent="center" alignItems="flex-start">
                {torch.available && !error ? (
                  <GlassButton
                    label="Torche"
                    activeState={torch.isOn}
                    onClick={torch.toggle}
                    icon={torch.isOn ? <FlashOnIcon /> : <FlashOffIcon />}
                  />
                ) : null}
                {camera.canSwitch && !error ? (
                  <GlassButton label="Caméra" onClick={camera.switch} icon={<SwitchCameraIcon />} />
                ) : null}
                <GlassButton
                  label="Historique"
                  activeState={historyMode}
                  accent={HISTORY_ACCENT}
                  onClick={toggleHistoryMode}
                  icon={historyMode ? <QrCodeScannerIcon /> : <HistoryIcon />}
                />
              </Stack>

              {historyMode ? (
                <Button
                  variant="outlined"
                  onClick={toggleHistoryMode}
                  fullWidth
                  sx={{
                    maxWidth: 420,
                    color: '#FFFFFF',
                    borderColor: 'rgba(255,255,255,0.35)',
                    '&:hover': { borderColor: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.08)' },
                  }}
                >
                  Retour au mode normal
                </Button>
              ) : null}
            </Stack>
          </>
        )}
      </CameraScanner>
    </Box>
  )
}

export default ScanView
